import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { fetchUserBuildings } from "../../api/buildingsApi";
import { useBuilding } from "../../context/BuildingContext";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";
import { Building } from "@akfdev-com/shared/src/models";

const BuildingsList: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [filteredBuildings, setFilteredBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { getToken } = useAuth();
  const { setCurrentBuilding } = useBuilding();

  useEffect(() => {
    const loadBuildings = async () => {
      try {
        setLoading(true);
        const token = (await getToken()) || "";
        const data = await fetchUserBuildings(token);
        if (!Array.isArray(data) || data.length === 0) {
          setBuildings([]);
          setFilteredBuildings([]);
          setLoading(false);
          return;
        }
        // Spread each Building so additive optional fields (structuredAddress,
        // location, notificationPreferences, status, etc.) survive to the
        // list view. Historically this map picked ~7 fields by hand, which
        // silently dropped everything else. Defensive default kept for
        // `unitsCount` so a legacy record with no count still renders.
        const buildingsNormalized: Building[] = data.map((b) => ({
          ...b,
          unitsCount: b.unitsCount ?? 0,
        }));
        setBuildings(buildingsNormalized);
        setFilteredBuildings(buildingsNormalized);
      } catch (err) {
        setError("Failed to load buildings. Please try again later.");
        console.error("Error loading buildings:", err);
        setBuildings([]);
        setFilteredBuildings([]);
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();
  }, [getToken]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = buildings.filter(
        (building) =>
          building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          building.address.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredBuildings(filtered);
    } else {
      setFilteredBuildings(buildings);
    }
  }, [searchTerm, buildings]);

  const handleBuildingSelect = (building: Building) => {
    setCurrentBuilding({
      id: building.id,
      name: building.name,
      address: building.address,
      role: building.role,
    });
  };

  if (loading) {
    return <Spinner message="Loading buildings..." />;
  }

  return (
    <div className="">
      <PageHeader
        title="Buildings"
        description="Manage all your properties in one place"
        actions={
          <Link
            to="/dashboard/buildings/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Add Building
          </Link>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      <div className="mt-6 mb-8">
        <SearchInput
          placeholder="Search buildings by name or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
        />
      </div>

      {filteredBuildings.length === 0 ? (
        <EmptyState
          title="No buildings found"
          description={
            searchTerm
              ? "No buildings match your search criteria."
              : "You don't have any buildings yet."
          }
          actionText={searchTerm ? "Clear search" : "Add your first building"}
          actionHref={searchTerm ? undefined : "/dashboard/buildings/new"}
          onAction={searchTerm ? () => setSearchTerm("") : undefined}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
        />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredBuildings.map((building) => (
              <li key={building.id}>
                <Link
                  to={`/dashboard/buildings/${building.id}`}
                  className="block hover:bg-gray-50"
                  onClick={() => handleBuildingSelect(building)}
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-primary-600 truncate">
                          {building.name}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex gap-1">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800">
                            {building.role}
                          </p>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className="text-sm text-gray-500">
                          {building.unitsCount}{" "}
                          {building.unitsCount === 1 ? "unit" : "units"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          <svg
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {building.address}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <svg
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>
                          Added on{" "}
                          <time dateTime={building.createdAt}>
                            {new Date(building.createdAt).toLocaleDateString()}
                          </time>
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BuildingsList;
